import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import { Check, ChevronDown, Filter } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useCategoriesQuery } from '../../data/categories.queries';

type Filters = {
  priceRange: [number, number];
  sizes: string[];
};

type OpenSections = {
  categories: boolean;
  price: boolean;
  sizes: boolean;
};

type FilterSidebarProps = {
  onFilterChange: (filters: Filters) => void;
  onUserFilterChange?: () => void;
  onUserPriceChange?: () => void;
  onCategoryChange?: (category: string) => void;
  maxPrice?: number;
  activeCategories?: string[];
};

const formatMNT = (value: number) =>
  `${new Intl.NumberFormat('mn-MN').format(Math.max(0, Math.round(value)))} ₮`;

const normalizeCategoryValue = (value: string) =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');

export default function FilterSidebar({
  onFilterChange,
  onUserFilterChange,
  onUserPriceChange,
  onCategoryChange,
  maxPrice = 2000,
  activeCategories = [],
}: FilterSidebarProps) {
  const { language } = useTheme();
  const sliderMax = Math.max(
    1000,
    Math.ceil((Number.isFinite(maxPrice) ? maxPrice : 2000) / 1000) * 1000
  );
  const previousSliderMaxRef = useRef(sliderMax);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, sliderMax]);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [openSections, setOpenSections] = useState<OpenSections>({
    categories: true,
    price: true,
    sizes: true,
  });

  const { data: categoriesData = [] } = useCategoriesQuery();
  const normalizedActiveCategories = useMemo(
    () =>
      activeCategories
        .map((category) => normalizeCategoryValue(category))
        .filter((category): category is string => Boolean(category)),
    [activeCategories]
  );

  const categories = categoriesData.map((cat) => ({
    name: cat.name,
    id: normalizeCategoryValue(cat.slug || cat.id),
  }));

  const sizeGroups = [
    ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
    ['30', '32', '34'],
    ['39', '40', '41', '42', '43'],
    ['One Size'],
  ];

  const t = {
    title: language === 'mn' ? 'Шүүлтүүр' : 'Filters',
    categories: language === 'mn' ? 'Ангилал' : 'Categories',
    price: language === 'mn' ? 'Үнийн хязгаар' : 'Price Range',
    sizes: language === 'mn' ? 'Хэмжээ' : 'Sizes',
    min: language === 'mn' ? 'Доод' : 'Min',
    max: language === 'mn' ? 'Дээд' : 'Max',
  };

  const normalizePriceRange = (minValue: number, maxValue: number): [number, number] => {
    const safeMin = Number.isFinite(minValue) ? minValue : 0;
    const safeMax = Number.isFinite(maxValue) ? maxValue : sliderMax;

    let nextMin = Math.max(0, Math.round(safeMin));
    let nextMax = Math.min(sliderMax, Math.max(0, Math.round(safeMax)));

    if (nextMin > sliderMax) nextMin = sliderMax;
    if (nextMin > nextMax) [nextMin, nextMax] = [nextMax, nextMin];

    return [nextMin, nextMax];
  };

  useEffect(() => {
    const previousSliderMax = previousSliderMaxRef.current;
    previousSliderMaxRef.current = sliderMax;

    setPriceRange((prev) => {
      const shouldExpandMax = prev[1] === previousSliderMax && sliderMax > previousSliderMax;
      const [nextMin, nextMax] = normalizePriceRange(
        prev[0],
        shouldExpandMax ? sliderMax : prev[1]
      );
      if (prev[0] === nextMin && prev[1] === nextMax) return prev;
      return [nextMin, nextMax];
    });
  }, [sliderMax]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      onFilterChange({
        priceRange,
        sizes: selectedSizes,
      });
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [priceRange, selectedSizes, onFilterChange]);

  const toggleCategory = (catId: string) => {
    onUserFilterChange?.();
    onCategoryChange?.(catId);
  };

  const toggleSize = (size: string) => {
    onUserFilterChange?.();
    setSelectedSizes((prev) =>
      prev.includes(size) ? prev.filter((item) => item !== size) : [...prev, size]
    );
  };

  const toggleSection = (section: keyof OpenSections) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleMinPrice = (event: ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    if (!Number.isFinite(value)) return;
    onUserFilterChange?.();
    onUserPriceChange?.();
    setPriceRange(normalizePriceRange(value, priceRange[1]));
  };

  const handleMaxPrice = (event: ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    if (!Number.isFinite(value)) return;
    onUserFilterChange?.();
    onUserPriceChange?.();
    setPriceRange(normalizePriceRange(priceRange[0], value));
  };

  return (
    <aside className="mb-6 w-full lg:sticky lg:top-24 lg:h-[calc(100vh-6rem)] lg:w-[285px] lg:flex-shrink-0 lg:overflow-y-auto lg:scrollbar-hide lg:pb-10 lg:pr-2">
      <button
        type="button"
        onClick={() => setIsMobileFiltersOpen((prev) => !prev)}
        className="mb-4 flex w-full items-center justify-between rounded-[15px] border border-[var(--ubuy-border-soft)] bg-white px-4 py-3 text-[var(--ubuy-title)] shadow-sm lg:pointer-events-none lg:mb-4"
        aria-expanded={isMobileFiltersOpen}
        aria-controls="catalog-filters-panel"
      >
        <span className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--ubuy-accent)] text-[var(--ubuy-title)]">
            <Filter size={17} />
          </span>
          <span className="text-base font-bold">{t.title}</span>
        </span>
        <ChevronDown
          size={17}
          className={`text-[var(--ubuy-muted)] transition-transform lg:hidden ${
            isMobileFiltersOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      <div
        id="catalog-filters-panel"
        className={`overflow-hidden transition-all duration-300 lg:overflow-visible ${
          isMobileFiltersOpen
            ? 'max-h-[2200px] opacity-100'
            : 'max-h-0 opacity-0 pointer-events-none lg:pointer-events-auto'
        } lg:max-h-none lg:opacity-100`}
      >
        <div className="space-y-4">
          <FilterSection
            title={t.categories}
            open={openSections.categories}
            onToggle={() => toggleSection('categories')}
          >
            <div className="max-h-[430px] space-y-1 overflow-y-auto pr-1">
              {categories.map((cat) => {
                const isSelected = normalizedActiveCategories.includes(cat.id);
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggleCategory(cat.id)}
                    className={`flex w-full items-center gap-3 rounded-[10px] border px-3 py-2.5 text-left transition ${
                      isSelected
                        ? 'border-[var(--ubuy-accent)] bg-[#fff7df] text-[var(--ubuy-title)]'
                        : 'border-transparent text-[var(--ubuy-muted)] hover:border-[var(--ubuy-border-soft)] hover:bg-[var(--ubuy-subtle-surface)] hover:text-[var(--ubuy-title)]'
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-[6px] border ${
                        isSelected
                          ? 'border-[var(--ubuy-accent)] bg-[var(--ubuy-accent)]'
                          : 'border-[#cbd5e1] bg-white'
                      }`}
                    >
                      <Check
                        size={12}
                        strokeWidth={4}
                        className={isSelected ? 'text-white opacity-100' : 'opacity-0'}
                      />
                    </span>
                    <span className={`text-sm ${isSelected ? 'font-bold' : 'font-medium'}`}>{cat.name}</span>
                  </button>
                );
              })}
            </div>
          </FilterSection>

          <FilterSection
            title={t.price}
            open={openSections.price}
            onToggle={() => toggleSection('price')}
          >
            <div className="grid grid-cols-2 gap-3">
              <label className="space-y-1">
                <span className="text-xs text-[var(--ubuy-muted)]">{t.min}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={sliderMax}
                  value={priceRange[0]}
                  onChange={handleMinPrice}
                  className="w-full rounded-[10px] border border-[var(--ubuy-border-soft)] bg-white px-3 py-2 text-sm font-semibold text-[var(--ubuy-title)] outline-none focus:border-[var(--ubuy-accent)]"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs text-[var(--ubuy-muted)]">{t.max}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={sliderMax}
                  value={priceRange[1]}
                  onChange={handleMaxPrice}
                  className="w-full rounded-[10px] border border-[var(--ubuy-border-soft)] bg-white px-3 py-2 text-sm font-semibold text-[var(--ubuy-title)] outline-none focus:border-[var(--ubuy-accent)]"
                />
              </label>
            </div>

            <div className="mt-4 flex items-center justify-between text-xs font-bold text-[var(--ubuy-title)]">
              <span>{formatMNT(priceRange[0])}</span>
              <span>{formatMNT(priceRange[1])}</span>
            </div>

            <div className="relative mx-1 mt-4 h-1.5 rounded-full bg-[#e5e7eb]">
              <div
                className="absolute h-full rounded-full bg-[var(--ubuy-accent)]"
                style={{
                  left: `${(priceRange[0] / sliderMax) * 100}%`,
                  right: `${100 - (priceRange[1] / sliderMax) * 100}%`,
                }}
              />
              <input
                type="range"
                min="0"
                max={sliderMax}
                value={priceRange[0]}
                onChange={handleMinPrice}
                className="dual-range-slider absolute h-full w-full appearance-none opacity-0"
              />
              <input
                type="range"
                min="0"
                max={sliderMax}
                value={priceRange[1]}
                onChange={handleMaxPrice}
                className="dual-range-slider absolute h-full w-full appearance-none opacity-0"
              />
              <span
                className="pointer-events-none absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-[var(--ubuy-accent)] bg-white shadow-sm"
                style={{ left: `${(priceRange[0] / sliderMax) * 100}%` }}
              />
              <span
                className="pointer-events-none absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-[var(--ubuy-accent)] bg-white shadow-sm"
                style={{ left: `${(priceRange[1] / sliderMax) * 100}%` }}
              />
            </div>

            <div className="mt-4 flex items-center justify-between text-xs text-[var(--ubuy-muted)]">
              <span>0 ₮</span>
              <span>Max: {formatMNT(sliderMax)}</span>
            </div>
          </FilterSection>

          <FilterSection
            title={t.sizes}
            open={openSections.sizes}
            onToggle={() => toggleSection('sizes')}
          >
            <div className="space-y-4">
              {sizeGroups.map((group, groupIndex) => (
                <div key={groupIndex}>
                  <div className="flex flex-wrap gap-2">
                    {group.map((size) => {
                      const isSelected = selectedSizes.includes(size);
                      return (
                        <button
                          key={size}
                          type="button"
                          onClick={() => toggleSize(size)}
                          className={`rounded-[10px] border px-3 py-2 text-xs font-bold transition ${
                            isSelected
                              ? 'border-[var(--ubuy-accent)] bg-[var(--ubuy-accent)] text-[var(--ubuy-title)]'
                              : 'border-[var(--ubuy-border-soft)] bg-[var(--ubuy-subtle-surface)] text-[var(--ubuy-muted)] hover:border-[var(--ubuy-accent)] hover:bg-white hover:text-[var(--ubuy-title)]'
                          }`}
                        >
                          {size}
                        </button>
                      );
                    })}
                  </div>
                  {groupIndex < sizeGroups.length - 1 ? (
                    <div className="my-4 h-px w-full bg-[var(--ubuy-border-soft)]" />
                  ) : null}
                </div>
              ))}
            </div>
          </FilterSection>
        </div>
      </div>
    </aside>
  );
}

function FilterSection({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <section className="rounded-[15px] border border-[var(--ubuy-border-soft)] bg-white p-4 shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between text-[var(--ubuy-title)]"
      >
        <span className="text-base font-bold">{title}</span>
        <ChevronDown
          size={17}
          className={`text-[var(--ubuy-muted)] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className={`transition-all duration-300 ${
          open ? 'mt-4 max-h-[620px] opacity-100' : 'max-h-0 overflow-hidden opacity-0'
        }`}
      >
        {children}
      </div>
    </section>
  );
}
